/**
 * step1.ts — "Choose your base" (flow step 1). Renders the intro copy + the 4
 * base cards (image, charm-count limit, dynamic "from" price). Selecting a base
 * calls onChoose(kind); the flow (index.ts) then sets the base and advances to
 * step 2. All copy + images come from the section settings (catalog.bases / intro).
 */

import { baseFromPrice, type Catalog } from './data';
import { buildStepIndicator } from './steps';
import { wipeInHeading } from './anim';
import type { BaseKind } from './state';

const el = (tag: string, cls?: string) => { const e = document.createElement(tag); if (cls) e.className = cls; return e; };
const money = (n: number) => '€' + n.toFixed(2);
const initials = (t: string) => (t || '?').split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();

// Decorative PaoPao blob (icon-pack "paopao-two"), drawn behind the intro copy.
const PAOPAO_SHAPE = `<svg viewBox="0 0 477 477" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-full h-full"><path d="M393.551 82.2504C356.197 61.0458 307.1 66.9291 273.518 92.9463C245.969 114.354 237.636 123.896 207.656 98.6581C201.515 93.7571 195.141 88.8562 188.295 84.9219C159.471 68.0465 122.412 63.764 91.6287 77.838C60.3844 92.2343 36.7063 120.85 21.5013 151.321C-17.1848 229.955 17.9152 317.648 89.0427 363.825C133.093 393.688 189.891 408.505 243.937 407.279C346.371 407.268 450.018 342.792 468.136 252.35C476.718 213.444 468.716 170.629 447.608 136.686C434.197 114.93 415.991 94.7394 393.697 82.3285L393.562 82.2504H393.551Z" fill="#F5F5F5"/></svg>`;

// Same 3 brand backdrops as the product cards; cycled by card index so the 4 cards
// read one of each, then back to the first (0,1,2,0).
const HOVER_COLORS = ['#801431', '#C8D7EA', '#FFCA22'];
// Glow color for the glass button (lighten dark hues so the multiply glow reads).
export const glowColor = (hex: string): string => {
  const n = parseInt(hex.replace('#', ''), 16);
  let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  if ((r * 299 + g * 587 + b * 114) / 1000 < 128) {
    r = Math.round(r + (255 - r) * 0.57); g = Math.round(g + (255 - g) * 0.57); b = Math.round(b + (255 - b) * 0.57);
  }
  return `rgba(${r}, ${g}, ${b}, 0.4)`;
};

// Cursor-follow glow for glass buttons (mirrors snippets/button.liquid). Once per page.
let glassInit = false;
export function initGlassGlow(): void {
  if (glassInit) return;
  glassInit = true;
  document.addEventListener('pointermove', e => {
    const btn = (e.target as HTMLElement).closest('.btn--glass') as HTMLElement | null;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    btn.style.setProperty('--glow-x', e.clientX - r.left + 'px');
    btn.style.setProperty('--glow-y', e.clientY - r.top + 'px');
  });
}

export interface Step1Intro { heading: string; subtext: string; }

export interface MountStep1Opts {
  root: HTMLElement;
  catalog: Catalog;
  intro: Step1Intro;
  onChoose: (base: BaseKind) => void;
}

export function mountStep1({ root, catalog, intro, onChoose }: MountStep1Opts): void {
  root.innerHTML = '';
  initGlassGlow();

  const wrap = el('div', 'cfg cfg-step1 relative overflow-hidden bg-[#F5F5F5] flex flex-col md:grid md:grid-cols-12 md:grid-rows-[1fr] gap-4 md:gap-0 px-3 md:px-4 max-md:pt-20 pt-18 pb-16 md:pb-4');

  // Decorative PaoPao blob behind the top-left intro (bleeds off the corner).
  const shape = el('div', 'absolute -top-[14vh] -left-[6vw] w-[44vw] max-w-[640px] z-0 pointer-events-none');
  shape.innerHTML = PAOPAO_SHAPE;
  wrap.appendChild(shape);

  // Left copy (above the shape)
  const copy = el('div', 'relative z-10 row-1 col-start-1 col-span-3 flex flex-col gap-2');
  const h = el('h1', 'h2 m-0'); h.textContent = intro.heading;
  const sub = el('p', 'p m-0 text-foreground/70 max-w-[35ch]'); sub.textContent = intro.subtext;
  copy.append(h, sub);

  // Base cards (2×2)
  const grid = el('div', 'relative z-10 row-1 col-start-5 col-span-8 grid grid-cols-2 sm:grid-cols-2 md:grid-rows-2 gap-2 h-full');
  catalog.bases.forEach((base, i) => grid.appendChild(baseCard(base, i)));

  wrap.append(copy, grid);

  // Step indicator (bottom-left)
  const steps = el('div', 'absolute bottom-4 left-4 z-50');
  steps.appendChild(buildStepIndicator(1, () => {/* step 1 is the first; nothing earlier */}).el);
  wrap.appendChild(steps);

  root.appendChild(wrap);
  wipeInHeading(h);

  function baseCard(base: Catalog['bases'][number], index: number): HTMLElement {
    const from = baseFromPrice(catalog, base.kind);
    const tint = HOVER_COLORS[index % HOVER_COLORS.length];
    const soon = base.comingSoon; // coming-soon bases are shown but not selectable
    // pointer-events-none is a hard block on all hover + click, on top of the
    // removed handlers below — coming-soon bases are fully inert.
    const card = el('div',
      `group relative aspect-[unset] md:aspect-[4/3] md:aspect-auto rounded-sm overflow-hidden bg-detail/10 text-left ${soon ? 'cursor-not-allowed pointer-events-none' : 'cursor-pointer'}`);
    if (soon) {
      card.setAttribute('aria-disabled', 'true');
    } else {
      card.setAttribute('role', 'button');
      card.tabIndex = 0;
    }

    // Layers (back → front): Originals backdrop · hover-color tint (mix-blend) · front
    // cutout. The tint sits behind the transparent front image, like the product cards.
    const backdrop = catalog.backdropImage
      ? `<img class="absolute inset-0 w-full h-full object-cover opacity-90" style="object-position:${catalog.backdropFocal}" src="${catalog.backdropImage}" alt="">`
      : '';
    const tintLayer = `<span class="absolute inset-0 z-[1] mix-blend-color opacity-0 transition-opacity duration-200 ease-out ${soon ? '' : 'group-hover:opacity-80'}" style="background-color:${tint}"></span>`;
    const objPos = base.kind === 'necklace' ? 'object-[center_40%] max-md:w-[120%]! max-sm:max-w-[none] max-sm:left-[-10%]' : 'object-[center_40%]';
    const media = base.image
      ? `<img class="absolute inset-0 w-full h-full object-contain ${objPos} z-[2]" src="${base.image}" alt="">`
      : `<span class="absolute inset-0 z-[2] flex items-center justify-center text-foreground/30 text-4xl font-semibold">${initials(base.label)}</span>`;

    // "Coming soon" badge (top-left) — toggled per base from the section settings.
    const comingSoon = base.comingSoon
      ? `<span class="absolute top-2 left-2 z-[4] product-badge">Coming soon</span>`
      : '';

    // Glass CTA — shown on hover (expands in); glow follows the cursor (initGlassGlow).
    // Omitted for coming-soon bases (they aren't selectable).
    const cta = soon ? '' :
      `<div class="overflow-hidden max-h-0 opacity-0 transition-all duration-500 ease-out group-hover:max-h-20 group-hover:opacity-100">` +
        `<div class="px-3 pb-3">` +
          `<button type="button" style="--glow-color:${glowColor(tint)}" class="btn btn--glass btn--animate relative overflow-hidden w-full bg-white/50 text-black text-sm px-3 pt-2.25 pb-2 leading-none rounded-xs font-semibold uppercase flex items-center justify-center gap-x-2 backdrop-blur-sm">` +
            `<span class="btn__label relative z-1"><span class="btn__label-inner">Continue to design</span></span>` +
          `</button>` +
        `</div>` +
      `</div>`;

    card.innerHTML =
      backdrop + tintLayer + media + comingSoon +
      `<div class="absolute inset-x-0 bottom-0 z-[3]">` +
        // Mobile-only readability gradient: transparent → background grey, behind the text.
        `<span class="md:hidden absolute inset-0 -z-10 bg-gradient-to-t from-[#F5F5F560] to-transparent pointer-events-none"></span>` +
        `<div class="flex flex-col md:flex-row md:items-end justify-between gap-1 md:gap-2 px-3 md:px-4 pb-3 pt-8">` +
          `<div>` +
            `<div class="h5 text-foreground">${base.label}</div>` +
            `<div class="p text-foreground/70">Up to ${base.maxCharms} charms</div>` +
          `</div>` +
          `<div class="p text-foreground/70 shrink-0">from ${money(from)}</div>` +
        `</div>` +
        cta +
      `</div>`;

    // Coming-soon bases render but don't wire up selection.
    if (!soon) {
      const choose = () => onChoose(base.kind);
      card.addEventListener('click', choose);
      card.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(); } });
    }
    return card;
  }
}
